/**
 * Procedural Web Audio for the game.
 *
 * No samples or network resources are used.  The manager owns one lazy audio
 * graph, keeps effects and music on separate buses, and exposes cue-shaped
 * methods so the controller never needs to know about Web Audio nodes.
 */

export type AudioBus = 'master' | 'music' | 'effects';
export type AmbientTheme = string;

export interface AudioVolumes {
  master: number;
  music: number;
  effects: number;
}

export interface AudioManagerOptions extends Partial<AudioVolumes> {
  context?: AudioContext;
  muted?: boolean;
  quiet?: boolean;
  reducedMotion?: boolean;
  ambientTheme?: AmbientTheme;
  ambientIntensity?: number;
  autoStartAmbient?: boolean;
}

export interface AudioPreferences extends AudioVolumes {
  muted: boolean;
  quiet: boolean;
  reducedMotion: boolean;
}

export interface AudioCuePayload {
  pieceType?: number | string;
  size?: number;
  amount?: number;
  cascade?: number;
  special?: string;
  kind?: string;
  complete?: boolean;
  progress?: number;
  valid?: boolean;
  action?: string;
}

/** Structural compatibility with logic.MoveEvent; the logic layer is not imported. */
export interface AudioEventLike extends AudioCuePayload {
  kind: string;
  label?: string;
  color?: string;
}

type Timer = ReturnType<typeof setTimeout>;
type Interval = ReturnType<typeof setInterval>;
type Cleanup = () => void;
type Timbre = 'bell' | 'lumen' | 'soft' | 'pluck';

interface ToneOptions {
  frequency: number;
  endFrequency?: number;
  duration: number;
  volume: number;
  delay?: number;
  attack?: number;
  release?: number;
  timbre?: Timbre;
  waveform?: OscillatorType;
  filterFrequency?: number;
  filterEndFrequency?: number;
  filterQ?: number;
  pan?: number;
  vibrato?: number;
  vibratoDepth?: number;
  destination?: 'effects' | 'music';
  priority?: 'low' | 'normal' | 'high';
}

interface NoiseOptions {
  duration: number;
  volume: number;
  delay?: number;
  filterFrequency?: number;
  filterQ?: number;
  pan?: number;
  destination?: 'effects' | 'music';
  priority?: 'low' | 'normal' | 'high';
}

interface AmbientProfile {
  root: number;
  scale: number[];
  fifth: number;
  cutoff: number;
  pulse: number;
  wave: OscillatorType;
}

interface AudioGlobal {
  AudioContext?: new () => AudioContext;
  webkitAudioContext?: new () => AudioContext;
  document?: Document;
  matchMedia?: (query: string) => MediaQueryList;
}

const MAX_VOICES = 52;
const PIECE_ROOTS = [246.94, 277.18, 311.13, 349.23, 392, 440, 493.88];
const PROFILES: Record<string, AmbientProfile> = {
  garden: { root: 110, scale: [0, 3, 5, 7, 10, 12], fifth: 1.5, cutoff: 1050, pulse: 0.92, wave: 'sine' },
  verdant: { root: 123.47, scale: [0, 2, 4, 7, 9, 12], fifth: 1.5, cutoff: 1200, pulse: 0.86, wave: 'triangle' },
  orbit: { root: 98, scale: [0, 2, 5, 7, 9, 14], fifth: 1.498, cutoff: 850, pulse: 1.12, wave: 'sine' },
  forge: { root: 92.5, scale: [0, 3, 5, 7, 10, 12], fifth: 1.5, cutoff: 780, pulse: 1.03, wave: 'triangle' },
  tide: { root: 130.81, scale: [0, 2, 5, 7, 10, 12], fifth: 1.498, cutoff: 1400, pulse: 0.82, wave: 'sine' },
  archive: { root: 103.83, scale: [0, 1, 5, 7, 8, 12], fifth: 1.5, cutoff: 920, pulse: 1.28, wave: 'triangle' },
};

const clamp = (value: number, min = 0, max = 1): number =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
const semitone = (steps: number): number => Math.pow(2, steps / 12);
const finitePositive = (value: number | undefined, fallback: number): number =>
  value !== undefined && Number.isFinite(value) && value > 0 ? value : fallback;
const hash = (value: string): number => {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
};
const key = (value: string): string => value.trim().toLowerCase().replace(/[\s_-]+/g, '');
const scope = (): AudioGlobal => globalThis as unknown as AudioGlobal;
const reducedMotionByDefault = (): boolean => {
  try {
    return Boolean(scope().matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
};

export class AudioManager {
  private context: AudioContext | null;
  private readonly ownsContext: boolean;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private effectsGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;

  private masterVolume: number;
  private musicVolume: number;
  private effectsVolume: number;
  private muted: boolean;
  private quiet: boolean;
  private reducedMotion: boolean;

  private ambientTheme: AmbientTheme;
  private ambientIntensity: number;
  private ambientTension = 0;
  private ambientActive = false;
  private ambientRoot: GainNode | null = null;
  private ambientNodes: AudioNode[] = [];
  private ambientSources: AudioScheduledSourceNode[] = [];
  private ambientTimer: Interval | null = null;
  private ambientBeat = 0;
  private readonly autoStartAmbient: boolean;

  private noiseBuffer: AudioBuffer | null = null;
  private readonly voices = new Set<Cleanup>();
  private readonly timers = new Set<Timer>();
  private readonly waves = new Map<string, PeriodicWave>();
  private readonly lastCue = new Map<string, number>();
  private readonly lastVariant = new Map<string, number>();
  private variation = 0;
  private cascadeTimer: Timer | null = null;
  private gestureTarget: Document | null = null;
  private readonly gestureEvents = ['pointerdown', 'touchstart', 'keydown', 'click'];
  private readonly onGesture = (): void => { void this.resume(); };
  private disposed = false;

  public constructor(options: AudioManagerOptions = {}) {
    this.context = options.context ?? null;
    this.ownsContext = !options.context;
    this.masterVolume = clamp(options.master ?? 0.82);
    this.musicVolume = clamp(options.music ?? 0.68);
    this.effectsVolume = clamp(options.effects ?? 0.86);
    this.muted = Boolean(options.muted);
    this.quiet = Boolean(options.quiet);
    this.reducedMotion = options.reducedMotion ?? reducedMotionByDefault();
    this.ambientTheme = options.ambientTheme ?? 'garden';
    this.ambientIntensity = clamp(options.ambientIntensity ?? 0.46);
    this.autoStartAmbient = Boolean(options.autoStartAmbient);
    this.installGestureResume();
  }

  public static isSupported(): boolean {
    const host = scope();
    return Boolean(host.AudioContext ?? host.webkitAudioContext);
  }

  public get settings(): AudioPreferences {
    return {
      master: this.masterVolume,
      music: this.musicVolume,
      effects: this.effectsVolume,
      muted: this.muted,
      quiet: this.quiet,
      reducedMotion: this.reducedMotion,
    };
  }

  public get volumes(): AudioVolumes {
    return { master: this.masterVolume, music: this.musicVolume, effects: this.effectsVolume };
  }

  public get isMuted(): boolean { return this.muted; }
  public get isQuiet(): boolean { return this.quiet; }
  public get isReducedMotion(): boolean { return this.reducedMotion; }
  public get contextState(): AudioContextState | 'unavailable' { return this.context?.state ?? 'unavailable'; }

  /** Safe to call from a tap/click/key handler; also retried by gesture listeners. */
  public async resume(): Promise<boolean> {
    if (this.disposed) return false;
    const context = this.ensureGraph();
    if (!context) return false;
    try {
      if (context.state === 'suspended') await context.resume();
    } catch {
      // Autoplay policy will be retried by the next gesture.
    }
    if (context.state === 'running' && this.autoStartAmbient && !this.ambientActive) {
      this.startAmbient(this.ambientTheme, this.ambientIntensity);
    }
    return context.state === 'running';
  }

  public async unlock(): Promise<boolean> { return this.resume(); }

  public async suspend(): Promise<void> {
    if (this.context?.state !== 'running') return;
    try { await this.context.suspend(); } catch { /* closed contexts are already quiet */ }
  }

  public setMasterVolume(value: number): void { this.masterVolume = clamp(value); this.updateBuses(); }
  public setMusicVolume(value: number): void { this.musicVolume = clamp(value); this.updateBuses(); }
  public setEffectsVolume(value: number): void { this.effectsVolume = clamp(value); this.updateBuses(); }
  public setVolume(bus: AudioBus, value: number): void {
    if (bus === 'master') this.setMasterVolume(value);
    else if (bus === 'music') this.setMusicVolume(value);
    else this.setEffectsVolume(value);
  }

  public setMuted(value: boolean): void { this.muted = Boolean(value); this.updateBuses(); }
  public toggleMute(): boolean { this.setMuted(!this.muted); return this.muted; }
  public mute(): void { this.setMuted(true); }
  public unmute(): void { this.setMuted(false); }

  public setQuietMode(value: boolean): void {
    this.quiet = Boolean(value);
    this.updateBuses();
    this.refreshAmbientLevel();
    this.restartAmbientTimer();
  }

  public setQuiet(value: boolean): void { this.setQuietMode(value); }

  public setReducedMotion(value: boolean): void {
    this.reducedMotion = Boolean(value);
    this.updateBuses();
    this.refreshAmbientLevel();
    this.restartAmbientTimer();
  }

  public setPreferences(next: Partial<AudioManagerOptions>): void {
    const themeChanged = next.ambientTheme !== undefined && next.ambientTheme !== this.ambientTheme;
    if (next.master !== undefined) this.masterVolume = clamp(next.master);
    if (next.music !== undefined) this.musicVolume = clamp(next.music);
    if (next.effects !== undefined) this.effectsVolume = clamp(next.effects);
    if (next.muted !== undefined) this.muted = Boolean(next.muted);
    if (next.quiet !== undefined) this.quiet = Boolean(next.quiet);
    if (next.reducedMotion !== undefined) this.reducedMotion = Boolean(next.reducedMotion);
    if (next.ambientTheme !== undefined) this.ambientTheme = next.ambientTheme;
    if (next.ambientIntensity !== undefined) this.ambientIntensity = clamp(next.ambientIntensity);
    this.updateBuses();
    this.refreshAmbientLevel();
    this.restartAmbientTimer();
    if (themeChanged && this.ambientActive) {
      this.stopAmbient(0.3);
      this.startAmbient(this.ambientTheme, this.ambientIntensity);
    }
  }

  public setAmbientTheme(theme: AmbientTheme): void {
    const next = theme || 'garden';
    if (next === this.ambientTheme) return;
    this.ambientTheme = next;
    if (this.ambientActive) {
      this.stopAmbient(0.3);
      this.startAmbient(next, this.ambientIntensity);
    }
  }

  public setAmbientIntensity(value: number): void {
    this.ambientIntensity = clamp(value);
    this.refreshAmbientLevel();
  }

  public setAmbientTension(value: number): void {
    this.ambientTension = clamp(value);
    this.refreshAmbientLevel();
  }

  public startAmbient(theme = this.ambientTheme, intensity = this.ambientIntensity): void {
    if (this.disposed) return;
    const context = this.ensureGraph();
    if (!context || !this.musicGain) return;
    this.ambientTheme = theme || 'garden';
    this.ambientIntensity = clamp(intensity);
    if (this.ambientActive) { this.refreshAmbientLevel(); return; }

    const profile = this.profile(this.ambientTheme);
    const now = context.currentTime + 0.02;
    const nodes: AudioNode[] = [];
    const sources: AudioScheduledSourceNode[] = [];
    const root = context.createGain();
    const lowpass = context.createBiquadFilter();
    root.gain.value = 0.0001;
    lowpass.type = 'lowpass';
    lowpass.frequency.value = profile.cutoff;
    lowpass.Q.value = 0.42;
    lowpass.connect(root);
    root.connect(this.musicGain);
    nodes.push(root, lowpass);

    const pad = (frequency: number, amount: number, wave: OscillatorType, detune: number): void => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = wave;
      oscillator.frequency.value = frequency;
      oscillator.detune.value = detune;
      gain.gain.value = amount;
      oscillator.connect(gain);
      gain.connect(lowpass);
      nodes.push(oscillator, gain);
      sources.push(oscillator);
      try { oscillator.start(now); } catch { /* defensive for test doubles */ }
    };
    pad(profile.root, 0.24, profile.wave, -2.5);
    pad(profile.root * profile.fifth, 0.12, 'sine', 2.5);
    pad(profile.root * 2, 0.04, 'sine', -4);

    const lfo = context.createOscillator();
    const lfoGain = context.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = 0.075 + this.ambientIntensity * 0.045;
    lfoGain.gain.value = 100 + this.ambientTension * 170;
    lfo.connect(lfoGain);
    lfoGain.connect(lowpass.frequency);
    nodes.push(lfo, lfoGain);
    sources.push(lfo);
    try { lfo.start(now); } catch { /* defensive for test doubles */ }

    if (!this.quiet && !this.reducedMotion) {
      const noise = context.createBufferSource();
      const noiseFilter = context.createBiquadFilter();
      const noiseGain = context.createGain();
      noise.buffer = this.getNoiseBuffer(context);
      noise.loop = true;
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = 350;
      noiseFilter.Q.value = 0.36;
      noiseGain.gain.value = 0.006;
      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(root);
      nodes.push(noise, noiseFilter, noiseGain);
      sources.push(noise);
      try { noise.start(now); } catch { /* defensive for test doubles */ }
    }

    this.ambientNodes = nodes;
    this.ambientSources = sources;
    this.ambientRoot = root;
    this.ambientActive = true;
    this.refreshAmbientLevel(now);
    this.scheduleAmbientPhrase(now + 0.16);
    this.ambientTimer = setInterval(() => this.scheduleAmbientPhrase(), this.ambientInterval());
  }

  public startMusic(theme = this.ambientTheme, intensity = this.ambientIntensity): void {
    this.startAmbient(theme, intensity);
  }

  public stopAmbient(fade = 0.28): void {
    if (!this.ambientActive && this.ambientNodes.length === 0) return;
    if (this.ambientTimer !== null) { clearInterval(this.ambientTimer); this.ambientTimer = null; }
    const context = this.context;
    const nodes = this.ambientNodes.slice();
    const sources = this.ambientSources.slice();
    const root = this.ambientRoot;
    this.ambientNodes = [];
    this.ambientSources = [];
    this.ambientRoot = null;
    this.ambientActive = false;
    const length = Math.max(0, fade);
    const stopAt = context ? context.currentTime + length : 0;
    if (root && context && length > 0) {
      try {
        root.gain.cancelScheduledValues(context.currentTime);
        root.gain.setValueAtTime(Math.max(0.0001, root.gain.value), context.currentTime);
        root.gain.linearRampToValueAtTime(0.0001, stopAt);
      } catch { /* a closed context only needs disconnect */ }
    }
    for (const source of sources) {
      try { source.stop(stopAt); } catch { /* already stopped */ }
    }
    const release = (): void => this.disconnect(nodes);
    if (length > 0) this.defer(release, length * 1000 + 80);
    else release();
  }

  public stopMusic(fade = 0.28): void { this.stopAmbient(fade); }

  public select(pieceType?: number | string): void {
    if (!this.allow('select', 0.035)) return;
    const root = this.pieceFrequency(pieceType);
    const variant = this.choose('select', 4);
    const leap = [4, 5, 7, 9][variant];
    this.tone({ frequency: root, endFrequency: root * semitone(1), duration: 0.13, volume: 0.17, attack: 0.012, release: 0.06, timbre: 'soft', filterFrequency: 2200, pan: -0.12 });
    this.tone({ frequency: root * semitone(leap), duration: 0.17, volume: 0.095, delay: 0.036, attack: 0.014, release: 0.09, timbre: 'bell', filterFrequency: 3000, pan: 0.12 });
  }

  public playSelect(pieceType?: number | string): void { this.select(pieceType); }

  public hover(pieceType?: number | string): void {
    if (!this.allow('hover', 0.07)) return;
    this.tone({ frequency: this.pieceFrequency(pieceType) * 2, duration: 0.07, volume: 0.045, attack: 0.008, release: 0.04, timbre: 'soft', filterFrequency: 2600, pan: 0, priority: 'low' });
  }

  public swap(valid = true, pieceType?: number | string): void {
    if (!valid) { this.invalid(); return; }
    if (!this.allow('swap', 0.05)) return;
    const root = this.pieceFrequency(pieceType) * 0.84;
    const leap = [3, 4, 5][this.choose('swap', 3)];
    this.tone({ frequency: root, endFrequency: root * semitone(leap), duration: 0.19, volume: 0.18, attack: 0.01, release: 0.1, timbre: 'pluck', filterFrequency: 1800, filterEndFrequency: 4200, pan: -0.18 });
    this.tone({ frequency: root * semitone(leap + 4), duration: 0.11, volume: 0.075, delay: 0.075, attack: 0.01, release: 0.06, timbre: 'bell', filterFrequency: 3600, pan: 0.2 });
    this.noise({ duration: 0.025, volume: 0.04, filterFrequency: 1450, filterQ: 0.65, pan: -0.08, priority: 'low' });
  }

  public playSwap(valid = true, pieceType?: number | string): void { this.swap(valid, pieceType); }

  public invalid(): void {
    if (!this.allow('invalid', 0.11)) return;
    const root = [207.65, 220, 233.08][this.choose('invalid', 3)];
    this.tone({ frequency: root, endFrequency: root * semitone(-2), duration: 0.17, volume: 0.17, attack: 0.012, release: 0.08, timbre: 'soft', filterFrequency: 1100, filterEndFrequency: 700, pan: -0.12 });
    this.tone({ frequency: root * semitone(-3), duration: 0.2, volume: 0.1, delay: 0.065, attack: 0.015, release: 0.1, timbre: 'soft', filterFrequency: 900, pan: 0.1 });
  }

  public playInvalid(): void { this.invalid(); }

  public match(size = 3, pieceType?: number | string, cascade = 0): void {
    if (!this.allow('match', 0.045)) return;
    const count = Math.max(3, Math.min(9, Math.round(size)));
    const chain = Math.max(0, Math.min(10, Math.round(cascade)));
    const root = this.pieceFrequency(pieceType) * semitone(chain * 0.55);
    const motifs = [[0, 4, 7], [0, 3, 7], [0, 5, 9], [0, 2, 7]];
    const motif = motifs[this.choose('match', motifs.length)];
    const extra = count >= 5 ? 2 : count >= 4 ? 1 : 0;
    const gap = this.reducedMotion ? 0.066 : 0.048;
    for (let index = 0; index < motif.length + extra; index += 1) {
      const degree = motif[index % motif.length] + (index >= motif.length ? 12 : 0);
      this.tone({
        frequency: root * semitone(degree),
        duration: 0.19 + Math.min(0.07, chain * 0.006),
        volume: (count >= 5 ? 0.105 : count === 4 ? 0.092 : 0.078) * (index ? 0.84 : 1),
        delay: index * gap,
        attack: 0.009,
        release: 0.1,
        timbre: index ? 'bell' : 'pluck',
        filterFrequency: 1900 + count * 210 + chain * 110,
        filterEndFrequency: 2800 + count * 230,
        pan: index % 2 ? 0.16 : -0.16,
        vibrato: chain >= 3 ? 5.1 : undefined,
        vibratoDepth: chain >= 3 ? 3.5 : undefined,
      });
    }
    if (count >= 4) this.noise({ duration: count >= 5 ? 0.13 : 0.09, volume: count >= 5 ? 0.032 : 0.022, delay: gap * 1.7, filterFrequency: 2300 + count * 180, filterQ: 0.4, pan: 0, priority: 'low' });
  }

  public playMatch(size = 3, pieceType?: number | string, cascade = 0): void { this.match(size, pieceType, cascade); }

  /** A cascade gets a new motif, a higher register, and a wider final chord. */
  public cascade(level = 1): void {
    const step = Math.max(1, Math.min(12, Math.round(level)));
    this.setAmbientTension(Math.min(1, 0.12 + step * 0.075));
    this.resetCascadeDecay();
    if (!this.allow('cascade', 0.052)) return;
    const patterns = [[0, 3, 7], [0, 2, 5, 9], [0, 4, 7, 11], [0, 3, 7, 10, 14]];
    const pattern = patterns[(this.choose('cascade', patterns.length) + step) % patterns.length];
    const base = 185 * semitone(Math.min(8, step - 1) * 0.68);
    const gap = this.reducedMotion ? 0.064 : Math.max(0.045, 0.082 - step * 0.002);
    const count = Math.min(pattern.length + Math.floor(step / 4), 7);
    for (let index = 0; index < count; index += 1) {
      const degree = pattern[index % pattern.length] + (index >= pattern.length ? 12 : 0);
      this.tone({ frequency: base * semitone(degree), duration: 0.22 + Math.min(0.14, step * 0.008), volume: Math.min(0.14, 0.064 + step * 0.006) * (index === count - 1 ? 1.18 : 0.78), delay: index * gap, attack: 0.012, release: 0.11, timbre: index === count - 1 ? 'bell' : 'lumen', filterFrequency: 2100 + step * 170, filterEndFrequency: 3300 + step * 180, pan: index % 2 ? 0.2 : -0.2, priority: step >= 5 ? 'high' : 'normal' });
    }
    if (step >= 3) this.noise({ duration: 0.12 + step * 0.008, volume: Math.min(0.055, 0.022 + step * 0.003), delay: (count - 1) * gap, filterFrequency: 1500 + step * 150, filterQ: 0.35, pan: 0, priority: 'normal' });
  }

  public playCascade(level = 1): void { this.cascade(level); }

  public special(kind = 'nova', strength = 1, cascade = 0): void {
    const name = key(kind || 'nova');
    const force = clamp(strength, 0.4, 1.7);
    if (!this.allow(`special:${name}`, 0.09)) return;
    const root = 164.81 * semitone(Math.min(4, Math.max(0, cascade)) * 0.45);
    if (name.includes('beam') || name.includes('line')) {
      this.tone({ frequency: root * semitone(7), endFrequency: root * semitone(19), duration: 0.42, volume: 0.19 * force, attack: 0.012, release: 0.19, timbre: 'lumen', filterFrequency: 2500, filterEndFrequency: 5400, pan: name.includes('v') ? 0.24 : -0.24, vibrato: 6.2, vibratoDepth: 4 });
      this.tone({ frequency: root * semitone(19), duration: 0.22, volume: 0.1 * force, delay: 0.12, attack: 0.012, release: 0.12, timbre: 'bell', filterFrequency: 5000, pan: name.includes('v') ? -0.16 : 0.16 });
      return;
    }
    if (name.includes('prism') || name.includes('color')) {
      [0, 4, 7, 11, 14].forEach((degree, index) => this.tone({ frequency: root * semitone(degree), duration: 0.38 + index * 0.035, volume: (0.075 - index * 0.007) * force, delay: index * 0.045, attack: 0.015, release: 0.2, timbre: 'bell', filterFrequency: 2600 + index * 460, pan: index % 2 ? 0.25 : -0.25 }));
      return;
    }
    if (name.includes('burst') || name.includes('wrap') || name.includes('orbit')) {
      this.tone({ frequency: root * 0.55, endFrequency: root * 0.32, duration: 0.32, volume: 0.23 * force, attack: 0.008, release: 0.17, timbre: 'soft', filterFrequency: 850, filterEndFrequency: 420, pan: 0 });
      this.tone({ frequency: root * semitone(7), endFrequency: root, duration: 0.46, volume: 0.16 * force, delay: 0.03, attack: 0.01, release: 0.24, timbre: 'lumen', filterFrequency: 2300, filterEndFrequency: 1500, pan: 0.05 });
      this.noise({ duration: 0.18, volume: 0.05 * force, delay: 0.02, filterFrequency: 1150, filterQ: 0.4, pan: 0, priority: 'high' });
      return;
    }
    this.tone({ frequency: root * 0.5, endFrequency: root * 0.72, duration: 0.5, volume: 0.19 * force, attack: 0.016, release: 0.25, timbre: 'soft', filterFrequency: 1200, filterEndFrequency: 2100, pan: 0 });
    [0, 5, 9, 12].forEach((degree, index) => this.tone({ frequency: root * semitone(degree), duration: 0.42, volume: (0.095 - index * 0.012) * force, delay: 0.055 + index * 0.026, attack: 0.014, release: 0.22, timbre: index === 3 ? 'bell' : 'lumen', filterFrequency: 2800 + index * 360, pan: index % 2 ? 0.18 : -0.18 }));
  }

  public playSpecial(kind = 'nova', strength = 1, cascade = 0): void { this.special(kind, strength, cascade); }

  /** A combined-special cue has two overlapping chords and a resolving halo. */
  public combo(kind = 'fusion', magnitude = 1): void {
    const name = key(kind || 'fusion');
    const force = clamp(magnitude, 0.6, 1.8);
    if (!this.allow(`combo:${name}`, 0.18)) return;
    const root = name.includes('prism') || name.includes('color') ? 146.83 : 130.81;
    const first = name.includes('beam') ? [0, 4, 7, 12] : [0, 3, 7, 10, 14];
    const second = name.includes('beam') ? [2, 7, 11, 14] : [4, 7, 12, 16, 19];
    const gap = this.reducedMotion ? 0.055 : 0.075;
    first.forEach((degree, index) => this.tone({ frequency: root * semitone(degree), duration: 0.48, volume: (0.1 - index * 0.009) * force, delay: index * gap, attack: 0.012, release: 0.25, timbre: index ? 'lumen' : 'soft', filterFrequency: 1900 + index * 300, pan: index % 2 ? 0.29 : -0.29, priority: 'high' }));
    second.forEach((degree, index) => this.tone({ frequency: root * semitone(degree + 12), duration: 0.62, volume: (0.087 - index * 0.008) * force, delay: 0.24 + index * gap, attack: 0.016, release: 0.32, timbre: 'bell', filterFrequency: 2600 + index * 420, pan: index % 2 ? -0.24 : 0.24, priority: 'high' }));
    this.noise({ duration: 0.24, volume: 0.06 * force, delay: 0.19, filterFrequency: 1800, filterQ: 0.32, pan: 0, priority: 'high' });
    this.setAmbientTension(0.82);
    this.resetCascadeDecay(3500);
  }

  public playCombo(kind = 'fusion', magnitude = 1): void { this.combo(kind, magnitude); }

  public blocker(kind = 'glass', layers = 1): void {
    if (!this.allow('blocker', 0.065)) return;
    const depth = Math.max(1, Math.min(4, Math.round(layers)));
    const glass = key(kind).includes('glass');
    const root = 118 + (hash(key(kind || 'glass')) % 5) * 8;
    this.tone({ frequency: root * semitone(depth * 0.18), endFrequency: root * semitone(-1.2), duration: 0.2 + depth * 0.018, volume: 0.16 + depth * 0.014, attack: 0.006, release: 0.13, timbre: glass ? 'lumen' : 'soft', filterFrequency: glass ? 1700 : 780, filterEndFrequency: glass ? 900 : 520, pan: -0.05 });
    if (depth > 1) this.tone({ frequency: root * semitone(-5), duration: 0.15, volume: 0.065 + depth * 0.01, delay: 0.05, attack: 0.006, release: 0.1, timbre: 'soft', filterFrequency: 650, pan: 0.08 });
    this.noise({ duration: glass ? 0.08 : 0.12, volume: 0.032 + depth * 0.006, filterFrequency: glass ? 3200 : 620, filterQ: 0.55, pan: 0, priority: 'low' });
  }

  public playBlocker(kind = 'glass', layers = 1): void { this.blocker(kind, layers); }

  public objective(kind = 'progress', completeOrProgress: boolean | number = false, target = 1): void {
    const numberValue = typeof completeOrProgress === 'number' ? completeOrProgress : 0;
    const complete = typeof completeOrProgress === 'boolean' ? completeOrProgress : numberValue >= Math.max(1, target);
    if (!this.allow(complete ? 'objective:complete' : 'objective:step', complete ? 0.18 : 0.07)) return;
    const root = 261.63 * semitone((hash(key(kind || 'progress')) % 5) - 2);
    if (!complete) {
      this.tone({ frequency: root, endFrequency: root * semitone(2), duration: 0.17, volume: 0.12, attack: 0.012, release: 0.09, timbre: 'bell', filterFrequency: 2800, pan: 0 });
      return;
    }
    [0, 4, 7, 12].forEach((degree, index) => this.tone({ frequency: root * semitone(degree), duration: 0.32, volume: 0.105 - index * 0.014, delay: index * 0.065, attack: 0.012, release: 0.16, timbre: index === 3 ? 'bell' : 'lumen', filterFrequency: 2700 + index * 370, pan: index % 2 ? 0.14 : -0.14, priority: 'high' }));
  }

  public playObjective(kind = 'progress', complete = false, target = 1): void { this.objective(kind, complete, target); }

  public spawn(pieceType?: number | string): void {
    if (!this.allow('spawn', 0.035)) return;
    const root = this.pieceFrequency(pieceType) * 0.62;
    this.tone({ frequency: root, endFrequency: root * semitone(7), duration: 0.16, volume: 0.07, attack: 0.01, release: 0.09, timbre: 'soft', filterFrequency: 1600, filterEndFrequency: 2900, pan: 0, priority: 'low' });
  }

  public hazard(level = 1): void {
    if (!this.allow('hazard', 0.12)) return;
    const amount = clamp(level / 6, 0.25, 1.2);
    this.tone({ frequency: 92, endFrequency: 76, duration: 0.31, volume: 0.1 * amount, attack: 0.012, release: 0.18, timbre: 'soft', filterFrequency: 620, filterEndFrequency: 420, pan: -0.08 });
    this.tone({ frequency: 146.83, duration: 0.22, volume: 0.055 * amount, delay: 0.07, attack: 0.01, release: 0.13, timbre: 'lumen', filterFrequency: 1000, pan: 0.1 });
  }

  public reshuffle(): void {
    if (!this.allow('reshuffle', 0.3)) return;
    [0, 3, 7, 10].forEach((degree, index) => this.tone({ frequency: 196 * semitone(degree), endFrequency: 196 * semitone(degree + 2), duration: 0.27, volume: 0.06, delay: index * 0.085, attack: 0.012, release: 0.14, timbre: 'pluck', filterFrequency: 1800 + index * 220, pan: index % 2 ? 0.18 : -0.18 }));
  }

  public nearWin(): void {
    if (!this.allow('nearwin', 0.45)) return;
    this.tone({ frequency: 392, endFrequency: 440, duration: 0.22, volume: 0.1, attack: 0.014, release: 0.12, timbre: 'bell', filterFrequency: 3200, pan: 0 });
  }

  public win(stars = 3): void {
    if (!this.allow('win', 0.8)) return;
    this.setAmbientTension(0);
    const rating = Math.max(1, Math.min(3, Math.round(stars)));
    const notes = rating === 1 ? [0, 4, 7] : rating === 2 ? [0, 4, 7, 11] : [0, 4, 7, 11, 14];
    notes.forEach((degree, index) => this.tone({ frequency: 261.63 * semitone(degree), duration: 0.58 + index * 0.045, volume: 0.115 - Math.min(0.045, index * 0.009), delay: index * 0.105, attack: 0.017, release: 0.3, timbre: index === notes.length - 1 ? 'bell' : 'lumen', filterFrequency: 2500 + index * 410, pan: index % 2 ? 0.2 : -0.2, priority: 'high' }));
    this.tone({ frequency: 130.81, endFrequency: 196, duration: 0.68, volume: 0.085, delay: 0.04, attack: 0.024, release: 0.38, timbre: 'soft', filterFrequency: 900, filterEndFrequency: 1500, pan: 0, priority: 'high' });
  }

  public loss(): void {
    if (!this.allow('loss', 0.8)) return;
    [0, -3, -7].forEach((degree, index) => this.tone({ frequency: 293.66 * semitone(degree), endFrequency: 293.66 * semitone(degree - 1), duration: 0.4, volume: 0.095 - index * 0.015, delay: index * 0.14, attack: 0.02, release: 0.23, timbre: 'soft', filterFrequency: 1300 - index * 170, filterEndFrequency: 780 - index * 70, pan: index % 2 ? 0.12 : -0.12, priority: 'high' }));
  }

  public ui(action = 'click'): void {
    if (!this.allow(`ui:${key(action)}`, 0.055)) return;
    const sounds: Record<string, [number, number]> = { click: [523.25, 2], confirm: [659.25, 4], open: [392, 5], close: [329.63, -3], pause: [440, -2], back: [349.23, -4], tab: [587.33, 3], toggle: [493.88, 4] };
    const [frequency, interval] = sounds[key(action)] ?? sounds.click;
    this.tone({ frequency, endFrequency: frequency * semitone(interval * 0.22), duration: 0.105, volume: 0.08, attack: 0.008, release: 0.055, timbre: 'soft', filterFrequency: 2600, pan: 0, priority: 'low' });
  }

  public playUI(action = 'click'): void { this.ui(action); }

  public play(cue: string, payload?: AudioCuePayload): void;
  public play(event: AudioEventLike): void;
  public play(value: string | AudioEventLike, payload: AudioCuePayload = {}): void {
    if (typeof value !== 'string') { this.playEvent(value); return; }
    const cue = key(value);
    switch (cue) {
      case 'select': this.select(payload.pieceType); break;
      case 'swap': this.swap(payload.valid !== false, payload.pieceType); break;
      case 'invalid': this.invalid(); break;
      case 'match': case 'clear': this.match(payload.size ?? payload.amount ?? 3, payload.pieceType, payload.cascade ?? 0); break;
      case 'cascade': this.cascade(payload.cascade ?? payload.amount ?? payload.size ?? 1); break;
      case 'special': this.special(payload.special ?? payload.kind ?? 'nova', payload.amount ?? 1, payload.cascade ?? 0); break;
      case 'combo': this.combo(payload.special ?? payload.kind ?? 'fusion', payload.amount ?? 1); break;
      case 'blocker': case 'damage': this.blocker(payload.kind ?? 'glass', payload.amount ?? 1); break;
      case 'objective': this.objective(payload.kind ?? 'progress', payload.complete ?? false, payload.progress ?? payload.amount ?? 1); break;
      case 'spawn': this.spawn(payload.pieceType); break;
      case 'hazard': this.hazard(payload.amount ?? 1); break;
      case 'reshuffle': this.reshuffle(); break;
      case 'nearwin': this.nearWin(); break;
      case 'win': this.win(payload.amount ?? 3); break;
      case 'loss': case 'lose': this.loss(); break;
      case 'ui': this.ui(payload.action ?? payload.kind ?? 'click'); break;
      default: break;
    }
  }

  public playEvent(event: AudioEventLike): void {
    const kind = key(event.kind);
    switch (kind) {
      case 'select': this.select(event.pieceType ?? event.color); break;
      case 'swap': this.swap(event.valid !== false, event.pieceType ?? event.color); break;
      case 'invalid': this.invalid(); break;
      case 'match': this.match(event.amount ?? event.size ?? 3, event.pieceType ?? event.color, event.cascade ?? 0); break;
      case 'clear': event.special ? this.special(event.special, event.amount ?? 1, event.cascade ?? 0) : this.match(event.amount ?? event.size ?? 3, event.pieceType ?? event.color, event.cascade ?? 0); break;
      case 'cascade': this.cascade(event.cascade ?? event.amount ?? 1); break;
      case 'special': this.special(event.special ?? 'nova', event.amount ?? 1, event.cascade ?? 0); break;
      case 'combo': this.combo(event.special ?? event.label ?? 'fusion', event.amount ?? 1); break;
      case 'damage': case 'blocker': this.blocker(event.kind, event.amount ?? 1); break;
      case 'objective': this.objective(event.label ?? 'progress', event.complete ?? false, event.progress ?? event.amount ?? 1); break;
      case 'spawn': this.spawn(event.pieceType ?? event.color); break;
      case 'hazard': this.hazard(event.amount ?? 1); break;
      case 'reshuffle': this.reshuffle(); break;
      case 'nearwin': this.nearWin(); break;
      case 'win': this.win(event.amount ?? 3); break;
      case 'loss': case 'lose': this.loss(); break;
      case 'ui': this.ui(event.action ?? event.label ?? 'click'); break;
      default: break;
    }
  }

  public dispose(): void {
    if (this.disposed) return;
    this.stopAmbient(0);
    this.disposed = true;
    this.detachGestureResume();
    if (this.cascadeTimer !== null) { clearTimeout(this.cascadeTimer); this.timers.delete(this.cascadeTimer); this.cascadeTimer = null; }
    for (const voice of Array.from(this.voices)) voice();
    for (const timer of Array.from(this.timers)) clearTimeout(timer);
    this.timers.clear();
    this.disconnect([this.masterGain, this.musicGain, this.effectsGain, this.compressor].filter(Boolean) as AudioNode[]);
    const context = this.context;
    this.masterGain = null;
    this.musicGain = null;
    this.effectsGain = null;
    this.compressor = null;
    this.waves.clear();
    this.noiseBuffer = null;
    if (context && this.ownsContext && context.state !== 'closed') {
      try { void context.close(); } catch { /* no-op */ }
    }
  }

  private ensureGraph(): AudioContext | null {
    if (this.disposed) return null;
    if (!this.context) {
      const host = scope();
      const Constructor = host.AudioContext ?? host.webkitAudioContext;
      if (!Constructor) return null;
      try { this.context = new Constructor(); } catch { return null; }
    }
    if (this.context.state === 'closed') return null;
    if (this.masterGain && this.musicGain && this.effectsGain) return this.context;
    try {
      const master = this.context.createGain();
      const music = this.context.createGain();
      const effects = this.context.createGain();
      const compressor = this.context.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.knee.value = 20;
      compressor.ratio.value = 5;
      compressor.attack.value = 0.004;
      compressor.release.value = 0.18;
      music.connect(master);
      effects.connect(master);
      master.connect(compressor);
      compressor.connect(this.context.destination);
      this.masterGain = master;
      this.musicGain = music;
      this.effectsGain = effects;
      this.compressor = compressor;
      this.updateBuses(this.context.currentTime);
      return this.context;
    } catch {
      this.context = null;
      return null;
    }
  }

  private updateBuses(time?: number): void {
    if (!this.context || !this.masterGain || !this.musicGain || !this.effectsGain) return;
    const now = time ?? this.context.currentTime;
    const musicScale = (this.quiet ? 0.56 : 1) * (this.reducedMotion ? 0.82 : 1);
    const effectsScale = (this.quiet ? 0.36 : 1) * (this.reducedMotion ? 0.62 : 1);
    this.ramp(this.masterGain.gain, this.muted ? 0 : this.masterVolume, now, 0.035);
    this.ramp(this.musicGain.gain, this.musicVolume * musicScale, now, 0.055);
    this.ramp(this.effectsGain.gain, this.effectsVolume * effectsScale, now, 0.022);
  }

  private installGestureResume(): void {
    const target = scope().document;
    if (!target) return;
    this.gestureTarget = target;
    for (const type of this.gestureEvents) {
      try { target.addEventListener(type, this.onGesture, { passive: true }); }
      catch { target.addEventListener(type, this.onGesture); }
    }
  }

  private detachGestureResume(): void {
    if (!this.gestureTarget) return;
    for (const type of this.gestureEvents) {
      try { this.gestureTarget.removeEventListener(type, this.onGesture); } catch { /* small DOM shims */ }
    }
    this.gestureTarget = null;
  }

  private profile(theme: AmbientTheme): AmbientProfile {
    const direct = PROFILES[key(theme)];
    if (direct) return direct;
    const names = Object.keys(PROFILES);
    return PROFILES[names[hash(theme) % names.length]];
  }

  private ambientLevel(): number {
    return 0.082 * this.ambientIntensity * (this.quiet ? 0.42 : 1) * (this.reducedMotion ? 0.7 : 1) * (1 + this.ambientTension * 0.34);
  }

  private refreshAmbientLevel(time?: number): void {
    if (!this.context || !this.ambientRoot) return;
    this.ramp(this.ambientRoot.gain, this.ambientLevel(), time ?? this.context.currentTime, 0.22);
  }

  private ambientInterval(): number { return this.quiet || this.reducedMotion ? 1650 : 1080; }

  private restartAmbientTimer(): void {
    if (!this.ambientActive) return;
    if (this.ambientTimer !== null) clearInterval(this.ambientTimer);
    this.ambientTimer = setInterval(() => this.scheduleAmbientPhrase(), this.ambientInterval());
  }

  private scheduleAmbientPhrase(time?: number): void {
    if (!this.ambientActive || this.disposed || !this.context || !this.musicGain || this.muted || this.musicVolume <= 0.001) return;
    const profile = this.profile(this.ambientTheme);
    const now = Math.max(this.context.currentTime + 0.012, time ?? this.context.currentTime + 0.012);
    const tension = this.ambientTension;
    const seed = hash(`${this.ambientTheme}:${this.ambientBeat}`);
    const index = (seed + this.ambientBeat * 3) % profile.scale.length;
    const degree = profile.scale[index];
    const root = profile.root * semitone(degree + (this.ambientBeat % 5 === 0 ? 12 : 0));
    const length = tension > 0.56 ? 3 : 2;
    const pulse = profile.pulse * (1 + tension * 0.1);
    this.ambientBeat += 1;
    for (let note = 0; note < length; note += 1) {
      const next = profile.scale[(index + note + (seed % 2)) % profile.scale.length];
      this.tone({ frequency: root * semitone(next - degree), duration: 0.48 + note * 0.06, volume: (0.022 + tension * 0.008) * (note ? 0.66 : 1), delay: note * 0.22 * pulse, attack: 0.035, release: 0.25, timbre: note === length - 1 ? 'bell' : 'soft', filterFrequency: profile.cutoff + 650 + tension * 700, pan: note % 2 ? 0.14 : -0.14, destination: 'music', priority: 'low' });
    }
  }

  private pieceFrequency(pieceType?: number | string): number {
    if (typeof pieceType === 'number' && Number.isFinite(pieceType)) return PIECE_ROOTS[Math.abs(Math.round(pieceType)) % PIECE_ROOTS.length];
    if (pieceType !== undefined) return PIECE_ROOTS[hash(String(pieceType)) % PIECE_ROOTS.length];
    return PIECE_ROOTS[this.choose('piece', PIECE_ROOTS.length)];
  }

  private allow(name: string, gap: number): boolean {
    if (this.disposed || this.muted || !this.ensureGraph()) return false;
    if (this.effectsVolume <= 0.001) return false;
    const now = this.context?.currentTime ?? 0;
    const previous = this.lastCue.get(name);
    if (previous !== undefined && now - previous < gap) return false;
    this.lastCue.set(name, now);
    return true;
  }

  private choose(name: string, count: number): number {
    if (count <= 1) return 0;
    const previous = this.lastVariant.get(name) ?? -1;
    let result = (hash(name) + this.variation + Math.floor((this.context?.currentTime ?? 0) * 31)) % count;
    this.variation += 1;
    if (result === previous) result = (result + 1 + this.variation) % count;
    this.lastVariant.set(name, result);
    return result;
  }

  private resetCascadeDecay(delay = 2300): void {
    if (this.cascadeTimer !== null) { clearTimeout(this.cascadeTimer); this.timers.delete(this.cascadeTimer); }
    this.cascadeTimer = this.defer(() => {
      this.cascadeTimer = null;
      this.setAmbientTension(this.ambientTension * 0.35);
    }, delay);
  }

  private tone(options: ToneOptions): void {
    if (this.disposed || this.muted) return;
    const context = this.ensureGraph();
    const destination = options.destination === 'music' ? this.musicGain : this.effectsGain;
    if (!context || !destination || options.volume <= 0 || (this.voices.size >= MAX_VOICES && options.priority !== 'high')) return;
    const duration = Math.max(0.026, finitePositive(options.duration, 0.1) * (this.reducedMotion ? 0.82 : 1));
    const start = context.currentTime + Math.max(0.004, options.delay ?? 0);
    const end = start + duration;
    const attack = Math.min(Math.max(0.004, options.attack ?? 0.014), duration * 0.42);
    const release = Math.min(Math.max(0.018, options.release ?? duration * 0.3), duration * 0.72);
    const nodes: AudioNode[] = [];
    let timer: Timer | null = null;
    let done = false;
    const cleanup: Cleanup = (): void => {
      if (done) return;
      done = true;
      if (timer !== null) { clearTimeout(timer); this.timers.delete(timer); }
      this.voices.delete(cleanup);
      this.disconnect(nodes);
    };
    try {
      const oscillator = context.createOscillator();
      const filter = context.createBiquadFilter();
      const gain = context.createGain();
      oscillator.type = options.waveform ?? 'triangle';
      if (options.timbre) {
        const wave = this.periodic(context, options.timbre);
        if (wave) oscillator.setPeriodicWave(wave);
      }
      filter.type = 'lowpass';
      filter.Q.value = options.filterQ ?? 0.65;
      const cutoff = finitePositive(options.filterFrequency, 3000);
      filter.frequency.setValueAtTime(cutoff, start);
      if (options.filterEndFrequency !== undefined) filter.frequency.linearRampToValueAtTime(finitePositive(options.filterEndFrequency, cutoff), end);
      oscillator.frequency.setValueAtTime(Math.max(24, options.frequency), start);
      if (options.endFrequency !== undefined) oscillator.frequency.linearRampToValueAtTime(Math.max(24, options.endFrequency), end);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(clamp(options.volume, 0, 1.2), start + attack);
      gain.gain.setValueAtTime(clamp(options.volume, 0, 1.2) * 0.76, Math.max(start + attack, end - release));
      gain.gain.linearRampToValueAtTime(0.0001, end);
      oscillator.connect(filter);
      filter.connect(gain);
      if (options.pan !== undefined && typeof context.createStereoPanner === 'function') {
        const panner = context.createStereoPanner();
        panner.pan.value = clamp(options.pan, -1, 1);
        gain.connect(panner);
        panner.connect(destination);
        nodes.push(panner);
      } else gain.connect(destination);
      nodes.push(oscillator, filter, gain);
      if (options.vibrato && options.vibratoDepth) {
        const lfo = context.createOscillator();
        const lfoGain = context.createGain();
        lfo.frequency.value = options.vibrato;
        lfoGain.gain.value = options.vibratoDepth;
        lfo.connect(lfoGain);
        lfoGain.connect(oscillator.frequency);
        nodes.push(lfo, lfoGain);
        lfo.start(start);
        lfo.stop(end + 0.012);
      }
      oscillator.onended = cleanup;
      this.voices.add(cleanup);
      oscillator.start(start);
      oscillator.stop(end + 0.018);
      timer = setTimeout(cleanup, Math.max(60, (end - context.currentTime) * 1000 + 180));
      this.timers.add(timer);
    } catch { cleanup(); }
  }

  private noise(options: NoiseOptions): void {
    if (this.disposed || this.muted || this.quiet || this.reducedMotion) return;
    const context = this.ensureGraph();
    const destination = options.destination === 'music' ? this.musicGain : this.effectsGain;
    if (!context || !destination || options.volume <= 0 || (this.voices.size >= MAX_VOICES && options.priority !== 'high')) return;
    const duration = Math.max(0.02, finitePositive(options.duration, 0.08));
    const start = context.currentTime + Math.max(0.004, options.delay ?? 0);
    const end = start + duration;
    const nodes: AudioNode[] = [];
    let timer: Timer | null = null;
    let done = false;
    const cleanup: Cleanup = (): void => {
      if (done) return;
      done = true;
      if (timer !== null) { clearTimeout(timer); this.timers.delete(timer); }
      this.voices.delete(cleanup);
      this.disconnect(nodes);
    };
    try {
      const source = context.createBufferSource();
      const filter = context.createBiquadFilter();
      const gain = context.createGain();
      source.buffer = this.getNoiseBuffer(context);
      filter.type = 'bandpass';
      filter.frequency.value = finitePositive(options.filterFrequency, 1600);
      filter.Q.value = options.filterQ ?? 0.45;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(clamp(options.volume, 0, 1.2), start + Math.min(0.008, duration * 0.22));
      gain.gain.linearRampToValueAtTime(0.0001, end);
      source.connect(filter);
      filter.connect(gain);
      if (options.pan !== undefined && typeof context.createStereoPanner === 'function') {
        const panner = context.createStereoPanner();
        panner.pan.value = clamp(options.pan, -1, 1);
        gain.connect(panner);
        panner.connect(destination);
        nodes.push(panner);
      } else gain.connect(destination);
      nodes.push(source, filter, gain);
      source.onended = cleanup;
      this.voices.add(cleanup);
      source.start(start);
      source.stop(end + 0.01);
      timer = setTimeout(cleanup, Math.max(60, (end - context.currentTime) * 1000 + 160));
      this.timers.add(timer);
    } catch { cleanup(); }
  }

  private periodic(context: AudioContext, timbre: Timbre): PeriodicWave | null {
    const cacheKey = `${timbre}:${context.sampleRate}`;
    const cached = this.waves.get(cacheKey);
    if (cached) return cached;
    try {
      if (typeof context.createPeriodicWave !== 'function') return null;
      const real = new Float32Array(9);
      const imag = new Float32Array(9);
      const harmonics: Record<Timbre, number[]> = {
        bell: [1, 0.08, 0.36, 0.02, 0.17, 0.01, 0.07, 0.005],
        lumen: [1, 0.22, 0.08, 0.035, 0.018, 0.01, 0.006, 0.003],
        soft: [1, 0.18, 0.025, 0.008, 0.002, 0, 0, 0],
        pluck: [1, 0.34, 0.16, 0.08, 0.035, 0.016, 0.008, 0.004],
      };
      harmonics[timbre].forEach((amount, index) => {
        real[index + 1] = amount * (index % 2 ? 0.35 : 0.7);
        imag[index + 1] = amount * (index % 2 ? 0.2 : 0.55);
      });
      const wave = context.createPeriodicWave(real, imag);
      this.waves.set(cacheKey, wave);
      return wave;
    } catch { return null; }
  }

  private getNoiseBuffer(context: AudioContext): AudioBuffer {
    if (this.noiseBuffer && this.noiseBuffer.sampleRate === context.sampleRate) return this.noiseBuffer;
    const length = Math.max(1, Math.floor(context.sampleRate * 2));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let state = 0x6d2b79f5;
    for (let index = 0; index < data.length; index += 1) {
      state = Math.imul(state ^ (state >>> 15), state | 1);
      state ^= state + Math.imul(state ^ (state >>> 7), state | 61);
      data[index] = ((((state ^ (state >>> 14)) >>> 0) / 4294967295) * 2 - 1) * 0.72;
    }
    this.noiseBuffer = buffer;
    return buffer;
  }

  private ramp(param: AudioParam, value: number, time: number, smoothing: number): void {
    try {
      param.cancelScheduledValues(time);
      param.setTargetAtTime(Math.max(0, value), time, smoothing);
    } catch {
      try { param.value = Math.max(0, value); } catch { /* minimal test doubles */ }
    }
  }

  private defer(callback: () => void, milliseconds: number): Timer {
    let timer: Timer;
    timer = setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, Math.max(0, milliseconds));
    this.timers.add(timer);
    return timer;
  }

  private disconnect(nodes: AudioNode[]): void {
    for (const node of nodes) {
      try { node.disconnect(); } catch { /* already disconnected */ }
    }
  }
}

export const createAudioManager = (options: AudioManagerOptions = {}): AudioManager => new AudioManager(options);
export default AudioManager;
